import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Check, Link2 } from 'lucide-react';
import type { UpdatePlatformSettingsDto } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useFooterSave, useSaveHold } from '../../components/settings/footerSave';
import { useSettingsTabKey } from '../../components/settings/settingsTabKey';
import { TAB_PARAM } from '../../components/settings/settingsTabsModel';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { FormField, Input } from '../../components/ui/Field';
import { Button } from '../../components/ui/Button';
import { useDialogs } from '../../components/ui/DialogProvider';
import { CHANGES_SAVED, useToast } from '../../components/ui/toast';
import { CopyableUrl, KeyStatus, MASKED, SetupLink, SetupSteps } from './integrationUi';
import { usePlatformSettingsSave, SaveRow } from './settingsSave';

const CANVA_CLIENT_ID_HINT =
  'From your Canva integration’s Configuration page (not a secret — shown in full here).';
const CANVA_SECRET_HINT =
  'Kept on our server (write-only — never shown again) and used only to complete the Canva OAuth exchange.';

/**
 * Install-level Canva integration. Canva Connect has NO personal access tokens — it is OAuth 2.0
 * against the ADMIN'S OWN Canva integration — so unlike the Figma card this one takes a Client ID
 * + Secret and then needs an explicit Connect round-trip (gated: creds must
 * be saved and unmodified, because the API reads them from PlatformSettings, not from this form).
 *
 * On Site Integrations the page's sticky Save footer saves the Client ID and Secret (useFooterSave),
 * and the card's own Save button is left out; rendered anywhere without a footer it keeps that
 * button. Connect, Disconnect and Copy act at once, as before. A connection made (the return from
 * Canva with `?canva=connected`) and a Disconnect confirm with the "Changes saved" toast. Leaving the
 * tab or the page waits for a Disconnect still running (useSaveHold).
 */
export function CanvaCard({
  clientId,
  clientSecretSet,
  connected,
  connectedUser,
  canvaRedirectUri,
}: {
  clientId: string | null;
  clientSecretSet: boolean;
  connected: boolean;
  connectedUser: string | null;
  canvaRedirectUri: string;
}) {
  const [id, setId] = useState(clientId ?? '');
  // Write-only secret: shows the MASKED mask when one is stored; focusing clears it to type anew.
  const [secret, setSecret] = useState('');
  const [secretTouched, setSecretTouched] = useState(false);
  const { saving, saved, setSaved, error, save } = usePlatformSettingsSave();

  const queryClient = useQueryClient();
  const { confirm } = useDialogs();
  const toast = useToast();
  const hold = useSaveHold();
  const [searchParams, setSearchParams] = useSearchParams();
  // The Site Integrations tab this card is on: kept in the query when `?canva=` is cleared below.
  const tabKey = useSettingsTabKey();

  // The API's browser callback, as the SERVER will send it. Canva rejects the authorize request
  // unless this EXACT string is registered on the integration, and the server builds it from
  // APP_BASE_URL — which is not necessarily the origin this page was loaded from (a preview host,
  // a custom domain, localhost). Showing window.location.origin would hand the admin a URL that
  // registers cleanly and then fails at connect time with a redirect_uri mismatch.
  const redirectUri = canvaRedirectUri;

  const secretDisplay = secretTouched ? secret : clientSecretSet ? MASKED : '';
  const idDirty = id.trim() !== (clientId ?? '').trim();
  const secretProvided = secretTouched && secret.trim() !== '';
  const dirty = idDirty || secretProvided;
  const hasSavedCreds = clientSecretSet && (clientId ?? '').trim() !== '';

  // Re-mask the write-only secret once a save lands (the Client ID is a plain value, kept).
  useEffect(() => {
    if (saved) {
      setSecret('');
      setSecretTouched(false);
    }
  }, [saved]);

  // Only what changed: the secret only when one was typed, so the stored one is never re-sent or cleared.
  const patch = (): UpdatePlatformSettingsDto => ({
    ...(idDirty ? { canvaClientId: id.trim() } : {}),
    ...(secretProvided ? { canvaClientSecret: secret.trim() } : {}),
  });

  const { inFooter } = useFooterSave({
    id: 'canva',
    dirty,
    saving,
    save: () => save(patch()),
    // Back to the saved Client ID and the masked secret.
    discard: () => {
      setId(clientId ?? '');
      setSecret('');
      setSecretTouched(false);
    },
  });

  // The card's own Save, drawn only where no footer saves it.
  const onSave = () =>
    void save(patch()).then((ok) => {
      if (ok) toast.show(CHANGES_SAVED);
    });

  // Return from Canva's consent screen: ?canva=connected|failed.
  const [returnNotice, setReturnNotice] = useState<'connected' | 'failed' | null>(null);
  const canvaParam = searchParams.get('canva');
  useEffect(() => {
    if (canvaParam !== 'connected' && canvaParam !== 'failed') return;
    setReturnNotice(canvaParam);
    // The round trip stored the connection: confirmed like a save. A failed one keeps its own line.
    if (canvaParam === 'connected') toast.show(CHANGES_SAVED);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('canva');
        // `?canva=` is what opened this card's tab: `?tab=` takes its place in this same write, so a
        // reload or a copied link still opens it (useSettingsTabKey).
        if (tabKey !== null) next.set(TAB_PARAM, tabKey);
        return next;
      },
      { replace: true },
    );
  }, [canvaParam, setSearchParams, toast, tabKey]);

  // Pick up the freshly-stored tokens / connected account after a successful round-trip.
  useEffect(() => {
    if (returnNotice === 'connected') {
      void queryClient.invalidateQueries({ queryKey: ['site-integrations'] });
    }
  }, [returnNotice, queryClient]);

  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const canConnect = hasSavedCreds && !dirty && !connecting;

  const onConnect = async () => {
    setConnecting(true);
    setConnectError(null);
    try {
      const { url } = await api.canvaConnect();
      // Leave the SPA for Canva's consent screen; `connecting` stays true.
      window.location.href = url;
    } catch (err) {
      setConnectError(
        err instanceof ApiRequestError ? err.message : 'Could not start the Canva connection.',
      );
      setConnecting(false);
    }
  };

  const disconnect = useMutation({
    mutationFn: () => api.canvaDisconnect(),
    onSuccess: async () => {
      setReturnNotice(null);
      await queryClient.invalidateQueries({ queryKey: ['site-integrations'] });
      await queryClient.invalidateQueries({ queryKey: ['site-audit'] });
      toast.show(CHANGES_SAVED);
    },
  });

  return (
    <Card>
      <CardHeader
        title="Canva Integration"
        description="Connect this install's Canva account to browse designs and assets."
      />
      <CardBody className="space-y-4">
        <SetupSteps
          title="Set up Canva"
          action={
            <SetupLink href="https://www.canva.com/developers/integrations">
              Open the Canva Developer Portal
            </SetupLink>
          }
          steps={[
            <>
              Create an integration in the{' '}
              <a
                href="https://www.canva.com/developers/integrations"
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-link hover:text-action-hover"
              >
                Canva Developer Portal
              </a>
              .
            </>,
            <>
              Under <span className="font-semibold">Scopes</span> enable{' '}
              <code className="rounded bg-card px-1 py-0.5 text-xs text-ink-strong ring-1 ring-line">
                asset:read
              </code>
              ,{' '}
              <code className="rounded bg-card px-1 py-0.5 text-xs text-ink-strong ring-1 ring-line">
                design:meta:read
              </code>{' '}
              and{' '}
              <code className="rounded bg-card px-1 py-0.5 text-xs text-ink-strong ring-1 ring-line">
                profile:read
              </code>
              .
            </>,
            <>
              Add this exact URL under <span className="font-semibold">Authentication</span> →{' '}
              <span className="font-semibold">Redirect URLs</span>:
              <CopyableUrl value={redirectUri} />
            </>,
            <>
              Paste the integration&apos;s <span className="font-semibold">Client ID</span> and{' '}
              <span className="font-semibold">Client Secret</span> below and{' '}
              <span className="font-semibold">Save</span>.
            </>,
            <>
              Click <span className="font-semibold">Connect Canva</span> and approve access.
            </>,
          ]}
        />

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ink-strong">Client Secret</span>
          <KeyStatus set={clientSecretSet} />
          {connected ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-success-tint dark:bg-success-dark-tint px-2 py-0.5 text-xs font-medium text-success dark:text-success-dark">
              <Check className="h-3.5 w-3.5" aria-hidden />
              {connectedUser ? `Connected as ${connectedUser}` : 'Connected'}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-chip px-2 py-0.5 text-xs font-medium text-ink-muted">
              Not connected
            </span>
          )}
        </div>

        <FormField label="Canva Client ID" htmlFor="canva-client-id" hint={CANVA_CLIENT_ID_HINT}>
          <Input
            id="canva-client-id"
            type="text"
            autoComplete="off"
            value={id}
            onChange={(e) => {
              setSaved(false);
              setId(e.target.value);
            }}
            placeholder="e.g. OC-AZxxxxxxxxxxxx"
          />
        </FormField>

        <FormField
          label={clientSecretSet ? 'Replace Canva Client Secret' : 'Canva Client Secret'}
          htmlFor="canva-client-secret"
          hint={CANVA_SECRET_HINT}
        >
          <Input
            id="canva-client-secret"
            type="password"
            autoComplete="off"
            value={secretDisplay}
            onFocus={() => {
              // First focus on a masked field clears the mask so the admin types a fresh secret.
              if (!secretTouched) {
                setSecretTouched(true);
                setSecret('');
              }
            }}
            onChange={(e) => {
              setSaved(false);
              setSecretTouched(true);
              setSecret(e.target.value);
            }}
            onBlur={() => {
              // Left untouched-empty → restore the mask (means "keep the stored secret").
              if (secretTouched && secret === '') setSecretTouched(false);
            }}
            placeholder={clientSecretSet ? MASKED : 'cnvca…'}
          />
        </FormField>

        <SaveRow inFooter={inFooter} saving={saving} saved={saved} dirty={dirty} error={error} onSave={onSave} />

        <div className="space-y-3 border-t border-line-soft pt-5">
          {returnNotice === 'connected' && (
            <p className="flex items-center gap-1.5 text-sm text-success dark:text-success-dark">
              <Check className="h-4 w-4" aria-hidden /> Canva connected.
            </p>
          )}
          {returnNotice === 'failed' && (
            <p className="text-sm text-red-600 dark:text-error-dark">
              Canva connection didn&apos;t complete. Please try connecting again.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => void onConnect()} loading={connecting} disabled={!canConnect}>
              <Link2 className="h-4 w-4" aria-hidden />
              {connected ? 'Reconnect Canva' : 'Connect Canva'}
            </Button>
            {connected && (
              <Button
                variant="secondary"
                loading={disconnect.isPending}
                onClick={async () => {
                  if (
                    await confirm({
                      title: 'Disconnect Canva',
                      message:
                        'Disconnect Canva for this install? Your Client ID and Secret stay saved — you can reconnect anytime.',
                      confirmLabel: 'Disconnect',
                      danger: true,
                    })
                  ) {
                    void hold(disconnect.mutateAsync());
                  }
                }}
                className="border-red-200 dark:border-error-dark/30 text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
              >
                Disconnect
              </Button>
            )}
            {!hasSavedCreds ? (
              <span className="text-sm text-ink-faint">Save the Client ID and Secret first.</span>
            ) : dirty ? (
              <span className="text-sm text-ink-faint">Save your changes before connecting.</span>
            ) : null}
            {connectError && <span className="text-sm text-red-600 dark:text-error-dark">{connectError}</span>}
            {disconnect.isError && (
              <span className="text-sm text-red-600 dark:text-error-dark">Could not disconnect Canva.</span>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
